import { newFieldId } from '../../shared/fieldIds';
import { makePaletteField, paletteEntry } from '../../shared/fieldTypes';

// react-sortablejs glue (build plan §5.9). Desktop only.
//
// Palette lists hold { id: 'palette-<type>', type } and are pull: 'clone'.
// The canvas list holds { id } per field and accepts drops. react-sortablejs
// 2.0.11 adds the palette's own item to the canvas list (the clone replaces
// it in the palette list, whose setList is a no-op), so a dropped item
// arrives either as a palette item or as an { isNew } clone.
// prepareSortableItems mints the field id before dispatch, so
// APPLY_SORTABLE (applySortable) stays pure.

export const PALETTE_PREFIX = 'palette-';
export const DRAG_DATA_TYPE = 'application/x-pp-field';

export const FIELD_GROUP = 'fb-fields';
export const PALETTE_GROUP = { name: FIELD_GROUP, pull: 'clone', put: false };
export const CANVAS_GROUP = { name: FIELD_GROUP, pull: false, put: true };
export const DRAG_HANDLE_CLASS = 'fb-drag-handle';
// The placeholder Sortable moves through the canvas while dragging (a card
// or a palette item). Canvas styles it as the design's .drop-line.
export const DROP_GHOST_CLASS = 'fb-drop-ghost';

export const paletteItems = (types) =>
  types.map((type) => ({ id: `${PALETTE_PREFIX}${type}`, type }));

export const paletteClone = (item) => ({
  id: newFieldId(),
  type: item.type,
  isNew: true,
});

export const canvasItems = (fields) => fields.map((f) => ({ id: f.id }));

// Drop guard: the only drag payload is an empty custom type, so there's no
// text/plain for an input to receive.
export const setDragData = (dataTransfer) => {
  dataTransfer.setData(DRAG_DATA_TYPE, '');
};

const isPaletteItem = (item) =>
  !!item &&
  typeof item.id === 'string' &&
  item.id.indexOf(PALETTE_PREFIX) === 0;

export const prepareSortableItems = (items, mint = newFieldId) =>
  items.map((item) =>
    isPaletteItem(item) ? { id: mint(), type: item.type, isNew: true } : item
  );

// Does this setList call change anything? react-sortablejs also calls it on
// mount and on choose/unchoose with the same order.
export const isSortableNoop = (fields, items) =>
  items.length === fields.length &&
  items.every((item, i) => item && !item.isNew && item.id === fields[i].id);

// fields + sortable items → { fields, newId } | null (nothing changed).
// Extra keys react-sortablejs adds (chosen, selected, filtered) are ignored.
export const applySortable = (fields, items) => {
  if (isSortableNoop(fields, items)) return null;
  const byId = {};
  fields.forEach((f) => {
    byId[f.id] = f;
  });
  const placed = {};
  const next = [];
  let newId = null;
  items.forEach((item) => {
    if (!item) return;
    if (item.isNew && paletteEntry(item.type) && !byId[item.id]) {
      next.push(makePaletteField(item.type, item.id));
      newId = item.id;
    } else if (byId[item.id] && !placed[item.id]) {
      placed[item.id] = true;
      next.push(byId[item.id]);
    }
  });
  // The canvas never gives fields away (pull: false); keep any the list
  // somehow lost rather than drop them.
  fields.forEach((f) => {
    if (!placed[f.id]) next.push(f);
  });
  const same =
    !newId &&
    next.length === fields.length &&
    next.every((f, i) => f === fields[i]);
  return same ? null : { fields: next, newId };
};
